import { useLoading } from "../context/LoadingContext";

import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../css/RelatorioAproveitamento.css";
import { FaArrowLeft, FaSearch } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import SelectBoxResponsavelPesquisa from "./SelectBoxResponsavelPesquisa";
import { useRestaurante } from "../context/RestauranteContext";
import { useUser } from "../context/UserContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import BackButton from "../components/BackButton";

registerLocale("pt-BR", ptBR);

const RelatorioAproveitamento = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // ID do restaurante da URL
  const { restauranteId } = useRestaurante(); // ID do restaurante do contexto
  const { user } = useUser(); // Acessa o contexto do usuário para o token
  const { setIsLoading } = useLoading();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  const [dataInicio, setDataInicio] = useState(firstDayOfMonth);
  const [dataFim, setDataFim] = useState(lastDayOfMonth);
  const [responsavelSelecionado, setResponsavelSelecionado] = useState("");
  const [nomeIngrediente, setNomeIngrediente] = useState("");
  const [reportData, setReportData] = useState([]);
  const [biAnalysis, setBiAnalysis] = useState(null);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false); // Novo estado de carregamento

  useEffect(() => {
    handleSearch();
  }, []); // Dispara a busca ao montar o componente

  const handleSearch = async () => {
    const toastId = toast.loading("Buscando relatório...");
    setIsLoading(true);

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    const inicio = dataInicio ? dataInicio : firstDayOfMonth;
    const fim = dataFim ? dataFim : lastDayOfMonth;

    const formattedDataInicio = inicio.toISOString().split("T")[0];
    const formattedDataFim = fim.toISOString().split("T")[0];

    let apiUrl = `${process.env.REACT_APP_BASE_URL}/api/v1/relatorios/aproveitamento?idRestaurante=${
      restauranteId || id
    }`;
    apiUrl += `&dataInicio=${formattedDataInicio}`;
    apiUrl += `&dataFim=${formattedDataFim}`;

    if (nomeIngrediente) {
      apiUrl += `&nomeIngrediente=${nomeIngrediente}`;
    }
    if (responsavelSelecionado) {
      apiUrl += `&idResponsavel=${responsavelSelecionado}`;
    }

    try {
      const token = user?.access_token;
      if (!token) {
        console.error("Token de autenticação não encontrado.");
        toast.update(toastId, {
          render: "Token de autenticação não encontrado.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const response = await fetch(apiUrl, {
        headers: {
          Authentication: token,
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const rawData = await response.json();

      let dataToProcess = [];
      if (Array.isArray(rawData)) {
        dataToProcess = rawData;
      } else if (
        typeof rawData === "object" &&
        rawData !== null &&
        Array.isArray(rawData.detalhes)
      ) {
        dataToProcess = rawData.detalhes;
      } else {
        dataToProcess = [];
      }

      const processedData = dataToProcess.map((item) => ({
        nomeIngrediente: item.nomeIngrediente,
        bruto: item.bruto || 0,
        limpo: item.limpo || 0,
        descarte: item.descarte != null ? item.descarte : 0.0,
        // Adicione outros campos relevantes para o gráfico aqui, se houver
      }));
      setReportData(processedData);

      // Análise de BI
      const totalBruto = processedData.reduce(
        (acc, item) => acc + (item.bruto || 0),
        0
      );
      const totalLimpo = processedData.reduce(
        (acc, item) => acc + (item.limpo || 0),
        0
      );
      const totalDescarte = processedData.reduce(
        (acc, item) => acc + (item.descarte || 0),
        0
      );
      const percentualAproveitamento =
        totalBruto > 0 ? ((totalLimpo / totalBruto) * 100).toFixed(2) : 0;

      setBiAnalysis({
        totalBruto,
        totalLimpo,
        totalDescarte,
        percentualAproveitamento,
      });
      toast.update(toastId, {
        render: "Relatório carregado com sucesso!",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Erro ao buscar relatório:", error);
      toast.update(toastId, {
        render: "Erro ao carregar relatório.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relatorio-container" id="filtro-relatorio-aproveitamento">
      <ToastContainer />
      <BackButton inline />
      <h2>Relatório de Aproveitamento de Ingredientes</h2>
      <div className="filtro-container-aproveitamento">
        <div className="filtro-group">
          <label>Data Início:</label>
          <DatePicker
            selected={dataInicio}
            onChange={(date) => setDataInicio(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de início"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Data Fim:</label>
          <DatePicker
            selected={dataFim}
            onChange={(date) => setDataFim(date)}
            dateFormat="dd/MM/yyyy"
            locale="pt-BR"
            placeholderText="Selecione a data de fim"
            className="date-picker"
          />
        </div>
        <div className="filtro-group">
          <label>Nome do Ingrediente:</label>
          <input
            type="text"
            value={nomeIngrediente}
            onChange={(e) => setNomeIngrediente(e.target.value)}
            placeholder="Nome do ingrediente"
            className="text-input"
          />
        </div>
        <div className="filtro-group">
          <label>Responsável:</label>
          <SelectBoxResponsavelPesquisa
            value={responsavelSelecionado}
            onChange={setResponsavelSelecionado}
          />
        </div>
        <button
          onClick={handleSearch}
          className={isLoadingLocal ? "search-button-loading" : "search-button"}
          disabled={isLoadingLocal}
        >
          <FaSearch /> {isLoadingLocal ? "Buscando..." : "Buscar"}
        </button>
      </div>

      {/* Conteúdo do relatório aqui */}
      <div className="relatorio-grafico-container">
        <h3>Análise de Aproveitamento</h3>
        {isLoadingLocal ? (
          <div className="loading-message">
            Carregando dados do relatório...
          </div>
        ) : reportData.length > 0 ? (
          <>
            <div className="bi-metrics">
              <p>
                <strong>Total Bruto:</strong>{" "}
                {biAnalysis?.totalBruto.toFixed(2)} KG
              </p>
              <p>
                <strong>Total Limpo:</strong>{" "}
                {biAnalysis?.totalLimpo.toFixed(2)} KG
              </p>
              <p>
                <strong>Total Descarte:</strong>{" "}
                {biAnalysis?.totalDescarte.toFixed(2)} KG
              </p>
              <p>
                <strong>% Aproveitamento:</strong>{" "}
                {biAnalysis?.percentualAproveitamento}%
              </p>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={reportData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nomeIngrediente" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="bruto" fill="#8884d8" name="Bruto" />
                <Bar dataKey="limpo" fill="#82ca9d" name="Limpo" />
                <Bar dataKey="descarte" fill="#ffc658" name="Descarte" />
              </BarChart>
            </ResponsiveContainer>
          </>
        ) : (
          <p className="no-data-message">
            Nenhum dado de aproveitamento encontrado para os filtros
            selecionados.
          </p>
        )}
      </div>
    </div>
  );
};

export default RelatorioAproveitamento;